import * as moment from 'moment';
import { Dispatch } from 'react-redux';
import { AuthActionCreators, IApplicationState } from '../';
import { IPost, IGet } from '../authorization';
import { ActionTypes } from '.';
import { IBaseResponse, ILogisticsResponse, ILogisticsRequest, ILogistics, ILogisticsOptionRequest, ILogisticsWithOption } from '../../models';
import { ActiveLogisticsPlan } from '../../enums';
import { IDelete } from '../authorization/actions';

export interface ILogisticsListResponse {
    data: {
        success: boolean;
        results: ILogistics[];
    }
}

export interface ILogisticsUpdate {
    chainId: number,
    clusterNumber: number,
    positionNumber: number,
    kidneyPickup: string,
    kidneyArrival: string,
    transportMethodActual: number,
    transportCostActual: number
}

export interface ILogisticsOptionResponse {
    data: {
        success: boolean;
        results: ILogisticsWithOption;
    }
}

export const ActionCreatorsLogisticsData = {
    getLogisticsDataByDonorRecipient: (donorId: number, recipientId: number) => async (dispatch: Dispatch<IApplicationState>): Promise<ILogisticsResponse> => {
        let options = {
            url: __API__ + '/position/logistics-data/donor/' + donorId + '/recipient/' + recipientId
        };

        return await dispatch(AuthActionCreators.apiGet(options));
    },
    getLogisticsDataByDonorRecipientForCourier: (donorId: number, recipientId: number) => async (dispatch: Dispatch<IApplicationState>): Promise<ILogisticsResponse> => {
        let options = {
            url: __API__ + '/position/logistics-data/donor/' + donorId + '/recipient/' + recipientId + '?courier=1'
        };

        return await dispatch(AuthActionCreators.apiGet(options));
    },
    getLogisticsDataByChainCluster: (chainId: number, clusterNumber: number) => async (dispatch: Dispatch<IApplicationState>): Promise<ILogisticsListResponse> => {
        let options = {
            url: `${__API__}/position/logistics-data/chain/${chainId}/cluster/${clusterNumber}`
        };

        return await dispatch(AuthActionCreators.apiGet(options));
    },
    getLogisticsDataByChainClusterPosition: (chainId: number, clusterNumber: number, positionNumber: number) => async (dispatch: Dispatch<IApplicationState>): Promise<ILogisticsListResponse> => {
        let options = {
            url: `${__API__}/position/logistics-data/chain/${chainId}/cluster/${clusterNumber}/position/${positionNumber}`
        };

        return await dispatch(AuthActionCreators.apiGet(options));
    },

    saveLogisticsData: (logistics: ILogisticsRequest) => (dispatch: Dispatch<IApplicationState>): Promise<IBaseResponse> => {
        let options: IPost = {
            url: __API__ + '/position/logistics-data',
            data: logistics
        };

        return dispatch(AuthActionCreators.apiPost(options)).then((response: IBaseResponse) => {
            if (response.data.success) {
                let update: ILogisticsUpdate = {
                    chainId: logistics.chainId,
                    clusterNumber: logistics.clusterNumber,
                    positionNumber: logistics.position,
                    kidneyPickup: moment(logistics.kidneyPickupDateTime).format('MM/DD/YY HH:mm'),
                    kidneyArrival: moment(logistics.kidneyArrivalDateTime).format('MM/DD/YY HH:mm'),
                    transportMethodActual: logistics.transportMethodActual,
                    transportCostActual: logistics.transportCostActual
                };

                dispatch(ActionCreatorsLogisticsData.updateLogistics(update));
            }

            return Promise.resolve(response);
        });
    },
    updateLogistics: (update: ILogisticsUpdate) => {
        return {
            type: ActionTypes.UPDATE_LOGISTICS,
            update
        };
    },
    saveActiveLogisticsPlan: (donorId: number, activeLogisticsPlan: ActiveLogisticsPlan) => async (dispatch: Dispatch<IApplicationState>): Promise<IBaseResponse> => {
        let options: IPost = {
            url: __API__ + '/donor/' + donorId + '/active-logistics-plan/' + activeLogisticsPlan,
        };

        return await dispatch(AuthActionCreators.apiPost(options));
    },

    getLogisticsOption: (chainId: number, clusterNumber: number, positionNumber: number) => async (dispatch: Dispatch<IApplicationState>): Promise<ILogisticsOptionResponse> => {
        let options = {
            url: __API__ + `/logistics/option/${chainId}/${clusterNumber}/${positionNumber}`
        };

        return await dispatch(AuthActionCreators.apiGet(options));
    },
    saveLogisticsOption: (request: ILogisticsOptionRequest, updateSwaps: boolean) => (dispatch: Dispatch<IApplicationState>): Promise<ILogisticsOptionResponse> => {
        let options: IPost = {
            url: __API__ + '/logistics/option',
            data: request
        };

        return dispatch(AuthActionCreators.apiPost(options)).then((response: ILogisticsOptionResponse) => {
            if (updateSwaps)
                dispatch(ActionCreatorsLogisticsData.updateSwapsForLogisticsPlan(request));

            return Promise.resolve(response);
        });
    },
    updateSwapsForLogisticsPlan: (update: ILogisticsOptionRequest) => {
        return {
            type: ActionTypes.UPDATE_SWAP_FOR_LOGISTICS_OPTION,
            update
        }
    },
    saveSelectedLogisticsOption: (optionId: number) => async (dispatch: Dispatch<IApplicationState>): Promise<IBaseResponse> => {
        let options: IPost = {
            url: __API__ + `/logistics/option/${optionId}/center`,
            data: null
        };

        return await dispatch(AuthActionCreators.apiPost(options));
    },
    deleteLogisticsOptions: (chainId: number, clusterNumber: number, positionMumber: number) => (dispatch: Dispatch<IApplicationState>): Promise<IBaseResponse> => {
        let options: IDelete = {
            url: __API__ + `/logistics/options/delete?chainId=${chainId}&clusterNumber=${clusterNumber}&positionNumber=${positionMumber}`
        };

        return dispatch(AuthActionCreators.apiDelete(options)).then((response: IBaseResponse) => {
            if (response.data.success) {
                dispatch(ActionCreatorsLogisticsData.updateSwapsForLogisticsOptionsDeleteReturn(chainId, clusterNumber, positionMumber));
            }

            return Promise.resolve(response);
        });
    },
    returnLogisticsOptions: (chainId: number, clusterNumber: number, positionMumber: number) => (dispatch: Dispatch<IApplicationState>): Promise<IBaseResponse> => {
        let options: IDelete = {
            url: __API__ + `/logistics/options/return-to-courier?chainId=${chainId}&clusterNumber=${clusterNumber}&positionNumber=${positionMumber}`
        };

        return dispatch(AuthActionCreators.apiDelete(options)).then((response: IBaseResponse) => {
            if (response.data.success) {
                dispatch(ActionCreatorsLogisticsData.updateSwapsForLogisticsOptionsDeleteReturn(chainId, clusterNumber, positionMumber));
            }

            return Promise.resolve(response);
        });
    },
    updateSwapsForLogisticsOptionsDeleteReturn: (chainId: number, clusterNumber: number, positionNumber: number) => {
        return {
            type: ActionTypes.UPDATE_SWAP_FOR_LOGISTICS_OPTION_DELETE_RETURN,
            chainId,
            clusterNumber,
            positionNumber
        }
    },
    sendCourierEmail: (chainId: number, clusterNumber: number, positionMumber: number, emailType: number) => async (dispatch: Dispatch<IApplicationState>): Promise<IBaseResponse> => {
        let options: IGet = {
            url: __API__ + `/email/courier/${chainId}/${clusterNumber}/${positionMumber}/${emailType}`
        };

        return await dispatch(AuthActionCreators.apiGet(options));
    }
}